import {
  GemIcon,
  GlobeIcon,
  GraduationCapIcon,
  LayersIcon,
  MapIcon,
  MountainSnowIcon,
  PickaxeIcon,
  ShellIcon,
  FlameIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  classroom: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Geology Fundamentals & Earth Systems: The Foundation',
    subtitle: 'The foundation your students need to understand everything else',
    classroom:
      'Your first introduction to geology can be transformative or tedious. This volume transforms technical concepts into something comprehensible and relevant from day one.',
    items: [
      'What is geology and why it matters for society',
      'The origin and age of Earth',
      "Earth's internal structure and layers",
      'The geosphere, atmosphere, hydrosphere, biosphere, and cryosphere',
      'Interactions between Earth systems',
      'Geological timescales and deep time',
      'Methods used in geological research',
      'Clear explanations with real-world examples',
    ],
    result:
      "Your students understand geology as a practical science that reveals Earth's past, explains present processes, and predicts future changes — not an impossible list of terms to memorize.",
    icon: GlobeIcon,
  },
  {
    number: '2',
    title: "Minerals, Crystals & The Rock Cycle: Earth's Building Blocks",
    subtitle: "Everything your students need to know about Earth's materials and how they transform",
    classroom:
      'This is where many students struggle. This volume gives you the materials to help them truly understand minerals, crystals, rocks, and the processes that continuously transform them.',
    items: [
      'Mineral properties and identification',
      'Crystal systems and crystal structures',
      'Mineral classification and families',
      'The rock cycle and its processes',
      'Igneous rocks: formation, types, and characteristics',
      'Sedimentary rocks: formation, types, and environments',
      'Metamorphic rocks: processes and characteristics',
      'Magma and crystallization processes',
      'Weathering and erosion of rocks',
      '15 worked mineral and rock identification examples',
    ],
    result: 'Your students learn to identify rocks and minerals with method and confidence.',
    icon: GemIcon,
  },
  {
    number: '3',
    title: "Plate Tectonics, Earthquakes & Volcanoes: Earth's Dynamics",
    subtitle: "How to teach Earth's internal dynamics clearly and practically",
    classroom:
      "Plate tectonics, earthquakes, and volcanoes are fascinating but complex. This volume gives you everything needed so your students understand how Earth's plates move and interact.",
    items: [
      'Continental drift and plate tectonics theory',
      'Tectonic plate types and boundaries',
      'Divergent, convergent, and transform boundaries',
      'Seafloor spreading and subduction',
      'Mountain building and orogeny',
      'Earthquake fundamentals and seismic waves',
      'Earthquake magnitude, intensity, and location',
      'Seismic hazards and earthquake forecasting',
      'Volcanic processes and eruption types',
      'Volcanic hazards and monitoring',
      'Distribution of earthquakes and volcanoes',
      '12 practical case studies ready for classroom use',
    ],
    result:
      'Your students understand why earthquakes and volcanoes occur and how geological hazards shape landscapes and affect populations.',
    icon: FlameIcon,
  },
  {
    number: '4',
    title: "Sedimentology, Stratigraphy & Geological Time: Reading Earth's History",
    subtitle: "Understanding sediments, rock layers, and Earth's timeline",
    classroom:
      'Sedimentology and geological time are complex but fundamental. This volume breaks them down without losing clarity.',
    items: [
      'Sediments and sedimentary processes',
      'Sedimentary environments and depositional systems',
      'Rock layers and stratigraphy fundamentals',
      'Stratigraphic principles and unconformities',
      'Relative dating methods',
      'Absolute dating techniques',
      'Radiometric dating and isotopes',
      'The geological timescale',
      'Major geological periods and eras',
      'Correlating rock layers across regions',
      'Interpreting depositional environments',
      'Reconstructing ancient landscapes',
      '10 practical stratigraphic analysis case studies',
    ],
    result: "Your students understand how geologists read Earth's history and reconstruct past environments.",
    icon: LayersIcon,
  },
  {
    number: '5',
    title: 'Paleontology & The Evolution of Life: Fossils Tell Stories',
    subtitle: 'Everything about fossils, fossilization, and the history of life',
    classroom:
      'Paleontology connects geology with biology and fascinates students. This volume gives you confidence to teach fossils and evolutionary history clearly.',
    items: [
      'What are fossils and how they form',
      'Fossilization processes and taphonomy',
      'Fossil types and preservation methods',
      'Fossil identification and classification',
      'Paleontology methods and techniques',
      "Major evolutionary events in Earth's history",
      'Precambrian life and the emergence of animals',
      'Paleozoic marine ecosystems',
      'The rise of fishes and amphibians',
      'The age of reptiles and dinosaurs',
      'The extinction of dinosaurs',
      'The rise of mammals and humans',
      'Biostratigraphy and index fossils',
      '8 paleontological discovery case studies',
    ],
    result: 'Your students understand how life has evolved and diversified over billions of years.',
    icon: ShellIcon,
  },
  {
    number: '6',
    title: "Structural Geology & Geological Mapping: Earth's Deformation",
    subtitle: 'Understanding rock deformation and interpreting geological structures',
    classroom:
      'Structural geology and mapping seem intimidating. This volume teaches the fundamentals clearly and practically.',
    items: [
      'Rock deformation and stress',
      'Brittle and ductile deformation',
      'Folds: types, formation, and characteristics',
      'Faults: types, mechanisms, and displacement',
      'Fractures and jointing',
      'Thrust faults and reverse faults',
      'Strike-slip faults and transform motion',
      'Foliation and lineation in metamorphic rocks',
      'Geological map interpretation',
      'Creating geological cross-sections',
      'Unconformities and structural relationships',
      'Dating structural features',
      'Field techniques and mapping methods',
      '10 real-world structural geology case studies',
    ],
    result: 'Your students understand how rocks deform and can interpret geological maps and cross-sections.',
    icon: MapIcon,
  },
  {
    number: '7',
    title: "Geomorphology, Soils & Earth's Landscapes: Sculpting the Surface",
    subtitle: 'Understanding weathering, erosion, and landscape formation',
    classroom:
      'Geomorphology explains the dramatic landscapes students see. This volume connects processes to visible features.',
    items: [
      'Weathering: mechanical, chemical, and biological',
      'Erosion and transportation processes',
      'Soil formation and soil horizons',
      'Soil composition and properties',
      'River systems and fluvial processes',
      'River erosion, transportation, and deposition',
      'Floodplains, deltas, and alluvial fans',
      'Mountain formation and erosion',
      'Coastal processes and shoreline features',
      'Glaciation and glacial landforms',
      'Desert landscapes and aeolian processes',
      'Karst landscapes and cave formation',
      'Landscape evolution and denudation',
      '12 geomorphological landscape case studies',
    ],
    result:
      "Your students understand how Earth's landscapes are created and continuously shaped by geological processes.",
    icon: MountainSnowIcon,
  },
  {
    number: '8',
    title: 'Applied, Environmental & Economic Geology: Geology in the Real World',
    subtitle: 'Exploring resources, hazards, and sustainable management',
    classroom:
      'This is the practical heart. Your students learn how geology applies to mining, energy, groundwater, and environmental challenges.',
    items: [
      'Mineral resources and ore deposits',
      'Energy resources: fossil fuels and alternatives',
      'Mining methods and environmental impacts',
      'Groundwater systems and aquifers',
      'Groundwater contamination and remediation',
      'Engineering geology and foundation stability',
      'Geological hazards: earthquakes, volcanoes, landslides',
      'Tsunami generation and propagation',
      'Coastal erosion and management',
      'Environmental contamination and remediation',
      'Geological waste disposal',
      'Sustainable resource management',
      'Climate change and geological processes',
      'Geothermal energy and resources',
      '15 real-world applied geology case studies',
    ],
    result:
      'Your students understand how geology applies to resource extraction, environmental challenges, and sustainable development.',
    icon: PickaxeIcon,
  },
  {
    number: '9',
    title: 'Teaching Geology: Lesson Planning, Activities & Projects',
    subtitle: 'Your complete guide as a geology educator',
    classroom:
      'This volume gives you exactly what you need to plan, organize, and evaluate your entire year of geology teaching.',
    items: [
      'The educational value of geology in science curricula',
      'Establishing clear learning objectives',
      'Organizing geology content by difficulty level',
      'Designing effective lesson sequences',
      'Inquiry-based geology education approaches',
      'Problem-based and project-based learning strategies',
      'Using real case studies in the classroom',
      'Teaching through local geological features',
      'Interdisciplinary connections and links',
      'Classroom discussions and guided debates',
      'Laboratory activities and rock identification',
      'Field trip planning and safety',
      'Assessment strategies for geology learning',
      'Rubrics and performance criteria',
      'Addressing common geology misconceptions',
      'Differentiation and inclusive instruction',
      '10 complete integrated capstone projects',
    ],
    result:
      'You\u2019ll never wonder "what do I teach today?" again. Everything is planned and contextualized for maximum student learning.',
    icon: GraduationCapIcon,
  },
];
